/** 动态灵敏度：与 nRF Python 工具 / 固件协议保持一致 */

export type DynamicPoint = [number, number]; // [speed_level 0~100, ratio]

export type DynamicNotifyState = {
  speedLevel: number;
  gainQ10: number;
  gain: number;
  rawMotion: boolean;
  pollingRateIndex: number;
  pollingRateHz: number;
};

export const POLLING_RATE_HZ = [125, 250, 500, 1000, 2000, 4000, 8000] as const;

export type DynamicSensitivityMode = 0 | 1 | 2 | 3; // classic | natural | jump | custom

export const DYNAMIC_POINT_COUNT = 5;
export const DYNAMIC_PAYLOAD_LEN = 22;
export const DYNAMIC_SPEED_MAX = 100;
export const DYNAMIC_GAIN_MIN = 0.1;
export const DYNAMIC_GAIN_MAX = 2;
export const DYNAMIC_GAIN_Q10_1X = 1024;
export const DYNAMIC_GAIN_Q10_MIN = 102;
export const DYNAMIC_GAIN_Q10_MAX = 2048;

/** 写入设备的 5 点预设（speed, ratio） */
export const PRESET_DEVICE_POINTS: Record<
  DynamicSensitivityMode,
  DynamicPoint[]
> = {
  0: [
    [0, 1],
    [25, 1.1],
    [50, 1.2],
    [75, 1.35],
    [100, 1.5],
  ],
  1: [
    [0, 1],
    [11, 1.45],
    [20, 1.5],
    [35, 1.5],
    [100, 1.5],
  ],
  2: [
    [0, 1],
    [9, 1],
    [21, 1.5],
    [24, 1.5],
    [100, 1.5],
  ],
  3: [
    [0, 1],
    [25, 1.1],
    [50, 1.2],
    [75, 1.35],
    [100, 1.5],
  ],
};

/** UI 展示直接使用固件 5 点坐标系（速度等级 0~100，倍率 0.1~2.0x） */
export const PRESET_DISPLAY_CURVES: Record<
  Exclude<DynamicSensitivityMode, 3>,
  DynamicPoint[]
> = {
  0: PRESET_DEVICE_POINTS[0],
  1: PRESET_DEVICE_POINTS[1],
  2: PRESET_DEVICE_POINTS[2],
};

export const CHART_PRESET = {
  xMax: DYNAMIC_SPEED_MAX,
  yMax: DYNAMIC_GAIN_MAX,
  showAreaFill: false,
};
export const CHART_CUSTOM = {
  xMax: DYNAMIC_SPEED_MAX,
  yMax: DYNAMIC_GAIN_MAX,
  showAreaFill: false,
};

const POINT_SPEED_MAX = DYNAMIC_SPEED_MAX;

export function getDefaultDynamicPoints(
  mode: DynamicSensitivityMode = 0
): DynamicPoint[] {
  return PRESET_DEVICE_POINTS[mode].map((p) => [...p] as DynamicPoint);
}

export function getDevicePoints(
  mode: DynamicSensitivityMode,
  customPoints?: DynamicPoint[] | null
): DynamicPoint[] {
  if (mode === 3) {
    const src =
      customPoints?.length === DYNAMIC_POINT_COUNT
        ? customPoints
        : PRESET_DEVICE_POINTS[3];
    return src.map(
      (p, i) =>
        [
          i === 0 ? 0 : (p[0] ?? PRESET_DEVICE_POINTS[3][i]?.[0] ?? 0),
          p[1] ?? 1,
        ] as DynamicPoint
    );
  }
  return getDefaultDynamicPoints(mode).map((p) => [...p] as DynamicPoint);
}

export function getDisplayPoints(
  mode: DynamicSensitivityMode,
  customPoints?: DynamicPoint[] | null
): DynamicPoint[] {
  if (mode === 3) return getDevicePoints(3, customPoints);
  return PRESET_DISPLAY_CURVES[mode as 0 | 1 | 2].map(
    (p) => [...p] as DynamicPoint
  );
}

export function encodePoint(speed: number, ratio: number): number[] {
  const speedWire = Math.max(0, Math.min(DYNAMIC_SPEED_MAX, Math.round(speed)));
  const gainQ10 = Math.max(
    DYNAMIC_GAIN_Q10_MIN,
    Math.min(
      DYNAMIC_GAIN_Q10_MAX,
      Math.round(
        Math.max(DYNAMIC_GAIN_MIN, Math.min(DYNAMIC_GAIN_MAX, ratio)) *
          DYNAMIC_GAIN_Q10_1X
      )
    )
  );
  return [
    speedWire & 0xff,
    (speedWire >> 8) & 0xff,
    gainQ10 & 0xff,
    (gainQ10 >> 8) & 0xff,
  ];
}

export function decodePoint(bytes: number[]): DynamicPoint {
  const speedWire = (bytes[0] ?? 0) | ((bytes[1] ?? 0) << 8);
  const gainQ10 = (bytes[2] ?? 0) | ((bytes[3] ?? 0) << 8);
  return [
    Math.max(0, Math.min(DYNAMIC_SPEED_MAX, speedWire)),
    Math.round(
      (Math.max(
        DYNAMIC_GAIN_Q10_MIN,
        Math.min(DYNAMIC_GAIN_Q10_MAX, gainQ10 || DYNAMIC_GAIN_Q10_1X)
      ) /
        DYNAMIC_GAIN_Q10_1X) *
        1000
    ) / 1000,
  ];
}

export function buildDynamicPayload(
  enable: number,
  points: DynamicPoint[]
): number[] {
  const payload = new Array(DYNAMIC_PAYLOAD_LEN).fill(0);
  payload[0] = enable ? 1 : 0;
  payload[1] = DYNAMIC_POINT_COUNT;
  for (let i = 0; i < DYNAMIC_POINT_COUNT; i++) {
    const [speed, ratio] = points[i] ?? [0, 1];
    const encoded = encodePoint(i === 0 ? 0 : speed, ratio);
    payload[2 + i * 2] = encoded[0];
    payload[3 + i * 2] = encoded[1];
    payload[12 + i * 2] = encoded[2];
    payload[13 + i * 2] = encoded[3];
  }
  return payload;
}

export function parseDynamicPayload(payload: number[]): {
  enable: number;
  points: DynamicPoint[];
} {
  const enable = payload[0] ?? 0;
  const points: DynamicPoint[] = [];
  for (let i = 0; i < DYNAMIC_POINT_COUNT; i++) {
    points.push(
      decodePoint([
        payload[2 + i * 2] ?? 0,
        payload[3 + i * 2] ?? 0,
        payload[12 + i * 2] ?? 0,
        payload[13 + i * 2] ?? 0,
      ])
    );
  }
  return { enable, points };
}

export function normalizeCustomPoints(points: DynamicPoint[]): DynamicPoint[] {
  const next = points.map((p) => [...p] as DynamicPoint);
  if (!next.length) return getDefaultDynamicPoints(3);
  next[0][0] = 0;
  next[0][1] = clampRatio(next[0][1] ?? 1);
  for (let i = 1; i < next.length; i++) {
    const prevSpeed = next[i - 1][0];
    // 新协议：速度等级非递减（允许相邻相等）
    let speed = Math.round(next[i][0] ?? prevSpeed);
    speed = Math.max(prevSpeed, Math.min(POINT_SPEED_MAX, speed));
    next[i][0] = speed;
    next[i][1] = clampRatio(next[i][1] ?? 1);
  }
  return next;
}

/** 解析 0xD4 Payload（去掉 CMD 后的 6 字节，或含 CMD 的 7 字节） */
export function parseDynamicNotify(
  data: ArrayLike<number>
): DynamicNotifyState | null {
  const bytes = Array.from(data);
  const offset = bytes[0] === 0xd4 ? 1 : 0;
  if (bytes.length < offset + 6) return null;

  const speedLevel = Math.max(
    0,
    Math.min(
      DYNAMIC_SPEED_MAX,
      (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8)
    )
  );
  const gainQ10Raw = (bytes[offset + 2] ?? 0) | ((bytes[offset + 3] ?? 0) << 8);
  const gainQ10 = Math.max(
    DYNAMIC_GAIN_Q10_MIN,
    Math.min(DYNAMIC_GAIN_Q10_MAX, gainQ10Raw || DYNAMIC_GAIN_Q10_1X)
  );
  const rawMotion = (bytes[offset + 4] ?? 0) !== 0;
  const pollingRateIndex = bytes[offset + 5] ?? 3;
  const pollingRateHz = POLLING_RATE_HZ[pollingRateIndex] ?? 1000;

  return {
    speedLevel,
    gainQ10,
    gain: Math.round((gainQ10 / DYNAMIC_GAIN_Q10_1X) * 1000) / 1000,
    rawMotion,
    pollingRateIndex,
    pollingRateHz,
  };
}

function clampRatio(ratio: number): number {
  return Math.max(
    DYNAMIC_GAIN_MIN,
    Math.min(DYNAMIC_GAIN_MAX, Math.round(ratio * 1000) / 1000)
  );
}

function linearSegment(
  a: DynamicPoint,
  b: DynamicPoint,
  segments: number
): DynamicPoint[] {
  const out: DynamicPoint[] = [];
  for (let i = 0; i <= segments; i++) {
    const r = i / segments;
    out.push([a[0] + r * (b[0] - a[0]), a[1] + r * (b[1] - a[1])]);
  }
  return out;
}

/** 固件按 5 点做 4 段线性插值，UI 显示保持同样逻辑 */
export function interpolateCurve(
  points: DynamicPoint[],
  segments = 24
): DynamicPoint[] {
  const sorted = [...points]
    .map((p) => [p[0] ?? 0, p[1] ?? 1] as DynamicPoint)
    .sort((a, b) => a[0] - b[0]);
  if (sorted.length === 0) return [];
  if (sorted.length === 1) return [sorted[0]];

  const result: DynamicPoint[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const seg = linearSegment(sorted[i], sorted[i + 1], segments);
    for (let j = i === 0 ? 0 : 1; j < seg.length; j++) result.push(seg[j]);
  }
  return result;
}

export const CHART_CUSTOM_X_MAX = CHART_CUSTOM.xMax;
export const CHART_CUSTOM_Y_MAX = CHART_CUSTOM.yMax;
export const MAX_POINT_SPEED = POINT_SPEED_MAX;

/** 拖拽/编辑时约束单个控制点（保持 speed 非递减、范围内） */
export function clampCustomPoint(
  index: number,
  speed: number,
  ratio: number,
  points: DynamicPoint[]
): DynamicPoint {
  const r = Math.max(
    DYNAMIC_GAIN_MIN,
    Math.min(CHART_CUSTOM_Y_MAX, Math.round(ratio * 1000) / 1000)
  );
  if (index === 0) return [0, r];
  let s = Math.round(Math.max(0, Math.min(POINT_SPEED_MAX, speed)));
  const prev = points[index - 1]?.[0] ?? 0;
  const isLast = index === points.length - 1;
  const next = isLast
    ? POINT_SPEED_MAX
    : (points[index + 1]?.[0] ?? POINT_SPEED_MAX);
  s = Math.max(s, prev);
  s = Math.min(s, isLast ? POINT_SPEED_MAX : next);
  return [s, r];
}

/** 给定输入速度，在插值曲线上取输出比率 */
export function ratioAtSpeed(speed: number, points: DynamicPoint[]): number {
  const curve = interpolateCurve(points);
  if (!curve.length) return 1;
  const first = curve[0];
  const last = curve[curve.length - 1];
  if (speed <= first[0]) return first[1];
  if (speed >= last[0]) return last[1];
  for (let i = 0; i < curve.length - 1; i++) {
    const [x0, y0] = curve[i];
    const [x1, y1] = curve[i + 1];
    if (speed >= x0 && speed <= x1) {
      return y0 + (x1 === x0 ? 0 : (speed - x0) / (x1 - x0)) * (y1 - y0);
    }
  }
  return last[1];
}

export function guessModeFromPoints(
  points: DynamicPoint[]
): DynamicSensitivityMode {
  for (const mode of [0, 1, 2] as const) {
    const preset = getDevicePoints(mode);
    if (
      preset.every(
        (p, i) =>
          Math.abs(p[0] - (points[i]?.[0] ?? 0)) < 0.6 &&
          Math.abs(p[1] - (points[i]?.[1] ?? 0)) < 0.06
      )
    )
      return mode;
  }
  return 3;
}
